// src/components/PlotCard.js
import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { formatDate, formatDateWithoutYear } from "../utils/dateUtils";
import apiClient from "../utils/apiClient";

const PlotCard = ({ plot, crops, onClick, onAssignFruit, year, simplified = false, onPlanCrop, rotationGroupNames = [], onEditPlanted, onPlantedUpdated }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [tipLoading, setTipLoading] = useState(false);
  const [tipError, setTipError] = useState(null);
  const [tipRecs, setTipRecs] = useState([]);
  const [tipCreating, setTipCreating] = useState(null); // crop_uuid
  const [tipCreatedMsg, setTipCreatedMsg] = useState(null);

  const formatRowsSuffix = (planted) => {
    if (planted?.rows == null) return "";
    const n = planted.rows;
    return ` : ${n} row${n === 1 ? "" : "s"}`;
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
    }
  };

  const fetchTipRecs = async () => {
    if (!plot?.plot_uuid || !year) return;
    setTipLoading(true);
    setTipError(null);
    setTipCreatedMsg(null);
    try {
      const res = await apiClient.get(
        `/plots/${plot.plot_uuid}/recommended-cover-crops?year=${year}`
      );
      setTipRecs(res.data || []);
    } catch (err) {
      setTipError(err.response?.data?.detail || "Failed to load cover crop recommendations");
      setTipRecs([]);
    } finally {
      setTipLoading(false);
    }
  };

  const handleOpenTip = (e) => {
    e.stopPropagation();
    setTipOpen(true);
    fetchTipRecs();
  };

  const handleCloseTip = (e) => {
    e?.stopPropagation?.();
    setTipOpen(false);
    setTipError(null);
    setTipCreatedMsg(null);
  };

  const handleCreateTask = async (e, cropUuid) => {
    e.stopPropagation();
    if (!plot?.plot_uuid || !year) return;
    setTipCreating(cropUuid);
    setTipError(null);
    setTipCreatedMsg(null);
    try {
      await apiClient.post(`/plots/${plot.plot_uuid}/cover-crop-task`, {
        year,
        cover_crop_uuid: cropUuid,
      });
      setTipCreatedMsg("Task created");
      if (onPlantedUpdated) {
        onPlantedUpdated();
      }
    } catch (err) {
      setTipError(err.response?.data?.detail || "Failed to create task");
    } finally {
      setTipCreating(null);
    }
  };

  return (
    <Card 
      sx={{ position: "relative" }} 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {!simplified && onClick && (
        <IconButton
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 10,
            backgroundColor: "var(--color-aged-paper)",
            border: "2px solid var(--color-border-vintage)",
            "&:hover": {
              backgroundColor: "var(--color-parchment)",
              borderColor: "var(--color-accent-gold)",
            },
          }}
          size="small"
          onClick={handleEditClick}
          className={`vintage-button plot-card-edit-button ${isHovered ? 'plot-card-edit-button-visible' : ''}`}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      <CardHeader
        title={
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: simplified && onClick ? 'pointer' : 'default' }}
              onClick={(e) => {
                if (!simplified || !onClick) return;
                e.stopPropagation();
                onClick();
              }}
            >
              <Typography variant="h6" component="span">
                {plot.name}
              </Typography>
              {simplified && rotationGroupNames.length > 0 && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    fontFamily: 'var(--font-serif)',
                    fontStyle: 'italic',
                    color: 'text.secondary',
                  }}
                >
                  ({rotationGroupNames.join(', ')})
                </Typography>
              )}
            </Box>

            {simplified && rotationGroupNames.length > 0 && (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenTip(e);
                  }}
                >
                  <InfoOutlinedIcon fontSize="inherit" />
                </IconButton>
                <Dialog
                  open={tipOpen}
                  onClose={handleCloseTip}
                  fullWidth
                  maxWidth="xs"
                >
                  <DialogTitle>Cover crops</DialogTitle>
                  <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
                      Showing cover crop that are eliglbe to sow together with the crops planned for this plot.
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {tipLoading ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">Loading…</Typography>
                      </Box>
                    ) : tipError ? (
                      <Typography variant="body2" color="error">
                        {tipError}
                      </Typography>
                    ) : tipRecs.length === 0 ? (
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        No significant eligible cover crops right now.
                      </Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {tipRecs.map((r) => (
                          <Box
                            key={r.crop_uuid}
                            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}
                          >
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {r.crop_name}
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                Score: {r.score > 0 ? `+${r.score}` : r.score}
                              </Typography>
                              {r.companion === true && (
                                <Typography variant="caption" sx={{ display: "block", opacity: 0.75 }}>
                                  Companion / undersown
                                </Typography>
                              )}
                              {r.cover_crop === true && r.companion !== true && (
                                <Typography variant="caption" sx={{ display: "block", opacity: 0.75 }}>
                                  After harvest (fertilizer)
                                </Typography>
                              )}
                              {r.cover_crop === true && r.companion === true && (
                                <Typography variant="caption" sx={{ display: "block", opacity: 0.75 }}>
                                  Cover crop + companion
                                </Typography>
                              )}
                            </Box>
                            {r.cover_crop == null && r.companion == null ? (
                              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                Set cover crop / companion
                              </Typography>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={tipCreating === r.crop_uuid}
                                onClick={(e) => handleCreateTask(e, r.crop_uuid)}
                              >
                                {tipCreating === r.crop_uuid ? "Adding…" : "Add task"}
                              </Button>
                            )}
                          </Box>
                        ))}
                        {tipCreatedMsg && (
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {tipCreatedMsg}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleCloseTip} className="vintage-button">
                      Close
                    </Button>
                  </DialogActions>
                </Dialog>
              </>
            )}
          </Box>
        }
      />
      <CardContent>
        {crops.length === 0 ? (
          plot.plot_type?.name?.toLowerCase() === 'fruit tree' ? (
            <Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                No fruit is selected for this tree
              </Typography>
              {onAssignFruit && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAssignFruit(plot);
                  }}
                  className="vintage-button"
                >
                  ✚ Assign Fruit
                </Button>
              )}
            </Box>
          ) : (
            onPlanCrop ? (
              <Table size="small" sx={{ '& .MuiTableCell-root': { paddingLeft: 0, paddingRight: 0 } }} className="plot-card-crops-table">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        No crops planted {year ? year : 'this year'}.
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {/* Add New Crop Pseudo Row */}
                  <TableRow 
                    className="add-crop-row"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onPlanCrop) {
                        onPlanCrop(plot);
                      }
                    }}
                    sx={{ 
                      cursor: "pointer",
                      '&:hover': {
                        backgroundColor: 'rgba(201, 169, 97, 0.1) !important'
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddIcon sx={{ fontSize: '1.2rem', color: 'var(--color-sepia)' }} />
                        <Typography sx={{ fontFamily: 'var(--font-serif)', color: 'var(--color-sepia)', fontStyle: 'italic' }}>
                          Add New Crop
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No crops planted {year ? year : 'this year'}.
              </Typography>
            )
          )
        ) : (
          <Table size="small" sx={{ '& .MuiTableCell-root': { paddingLeft: 0, paddingRight: 0 } }} className="plot-card-crops-table">
            <TableBody>
              {crops
                .sort((a, b) => new Date(a.date_planted) - new Date(b.date_planted))
                .map((c) => (
                  <TableRow 
                    key={c.planted_uuid}
                    className="plot-card-crop-row"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onEditPlanted) {
                        onEditPlanted(c);
                      }
                    }}
                    sx={{
                      cursor: onEditPlanted ? 'pointer' : 'default',
                      '&:hover': onEditPlanted ? {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      } : {},
                    }}
                  >
                    <TableCell>
                      {simplified 
                        ? `${formatDateWithoutYear(c.date_planted)} : ${c.crop.name}${formatRowsSuffix(c)}`
                        : `${formatDate(c.date_planted)} : ${c.crop.name}`
                      }
                    </TableCell>
                    {!simplified && (
                      <TableCell>
                        {c.rows ? `${c.rows} rows` : `${c.amount} plants`}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              {/* Add New Crop Pseudo Row - only show if onPlanCrop is provided and not fruit tree */}
              {onPlanCrop && plot.plot_type?.name?.toLowerCase() !== 'fruit tree' && (
                <TableRow 
                  className="add-crop-row"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onPlanCrop) {
                      onPlanCrop(plot);
                    }
                  }}
                  sx={{ 
                    cursor: "pointer",
                    '&:hover': {
                      backgroundColor: 'rgba(201, 169, 97, 0.1) !important'
                    }
                  }}
                >
                  <TableCell colSpan={simplified ? 1 : 2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AddIcon sx={{ fontSize: '1.2rem', color: 'var(--color-sepia)' }} />
                      <Typography sx={{ fontFamily: 'var(--font-serif)', color: 'var(--color-sepia)', fontStyle: 'italic' }}>
                        Add New Crop
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default PlotCard;
